import type { StyleSpecification } from "maplibre-gl";

export const MAPTILER_STYLE_URLS = {
  LIGHT: import.meta.env.VITE_MAPTILER_LIGHT_STYLE_URL,
  DARK: import.meta.env.VITE_MAPTILER_DARK_STYLE_URL,
  SATELLITE: import.meta.env.VITE_MAPTILER_SATELLITE_STYLE_URL,
  SIMPLE_LIGHT: import.meta.env.VITE_MAPTILER_SIMPLE_LIGHT_STYLE_URL,
  SIMPLE_DARK: import.meta.env.VITE_MAPTILER_SIMPLE_DARK_STYLE_URL,
} as const;

export type MaptilerStyleKey = keyof typeof MAPTILER_STYLE_URLS;

export const MAPTILER_STYLE_KEYS = {
  LIGHT: "LIGHT",
  DARK: "DARK",
  SATELLITE: "SATELLITE",
  SIMPLE_LIGHT: "SIMPLE_LIGHT",
  SIMPLE_DARK: "SIMPLE_DARK",
} as const satisfies Record<MaptilerStyleKey, MaptilerStyleKey>;

/** Flat canvas used while the real style URL loads, so no default basemap flashes. */
export const getPlaceholderStyle = (isDark: boolean): StyleSpecification => ({
  version: 8,
  sources: {},
  layers: [
    {
      id: "background",
      type: "background",
      paint: { "background-color": isDark ? "#1a1a1a" : "#f2f2f2" },
    },
  ],
});

const isLightStyle = (styleKey: MaptilerStyleKey) =>
  styleKey === "LIGHT" || styleKey === "SIMPLE_LIGHT";

export const getMaptilerStyleUrl = (
  styleKey: MaptilerStyleKey
): string | StyleSpecification => {
  const url = MAPTILER_STYLE_URLS[styleKey];
  if (!url) {
    console.error(
      `Missing MapTiler style URL for basemap key "${styleKey}" - falling back to a blank basemap`
    );
    return getPlaceholderStyle(!isLightStyle(styleKey));
  }
  return url;
};

/**
 * MapTiler rasterises vector-derived styles to PNG but serves satellite as JPEG;
 * the wrong extension 404s. Null when unset, so callers can skip the source.
 */
export const getMaptilerRasterTileUrl = (
  styleKey: MaptilerStyleKey
): string | null => {
  const url = MAPTILER_STYLE_URLS[styleKey];
  if (!url) {
    console.error(
      `Missing MapTiler style URL for basemap key "${styleKey}" - raster basemap skipped`
    );
    return null;
  }
  const extension = styleKey === "SATELLITE" ? "jpg" : "png";
  return url.replace("/style.json", `/{z}/{x}/{y}.${extension}`);
};

/** `BLANK_*` are minimal styles for pages that supply their own basemap layers. */
export const MAP_STYLES = {
  IMAGERY: getMaptilerStyleUrl(MAPTILER_STYLE_KEYS.SATELLITE),
  DEFAULT_DARK: getMaptilerStyleUrl(MAPTILER_STYLE_KEYS.DARK),
  DEFAULT_LIGHT: getMaptilerStyleUrl(MAPTILER_STYLE_KEYS.LIGHT),
  BLANK_DARK: getMaptilerStyleUrl(MAPTILER_STYLE_KEYS.SIMPLE_DARK),
  BLANK_LIGHT: getMaptilerStyleUrl(MAPTILER_STYLE_KEYS.SIMPLE_LIGHT),
} as const satisfies Record<string, string | StyleSpecification>;
